import React from 'react';

interface Account {
  id: number;
  name: string;
  institution: string;
  type: string;
}

interface GroupedAccountSelectProps {
  accounts: Account[];
  value: number | null;
  onChange: (accountId: number) => void;
  placeholder?: string;
  className?: string;
}

const GroupedAccountSelect: React.FC<GroupedAccountSelectProps> = ({
  accounts,
  value,
  onChange,
  placeholder = "Choose an account...",
  className = "form-select"
}) => {
  // Group accounts by type for better organization
  const groupedAccounts = accounts.reduce((groups, account) => {
    const type = account.type || 'other';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(account);
    return groups;
  }, {} as Record<string, Account[]>);

  // Define the order and labels for account types
  const accountTypeOrder = [
    { key: 'credit_card', label: '💳 Credit Cards', icon: '💳' },
    { key: 'checking', label: '🏦 Checking Accounts', icon: '🏦' },
    { key: 'savings', label: '💰 Savings Accounts', icon: '💰' },
    { key: 'investment', label: '📈 Investment Accounts', icon: '📈' },
    { key: 'loan', label: '🏠 Loans & Mortgages', icon: '🏠' },
    { key: 'line_of_credit', label: '📋 Lines of Credit', icon: '📋' },
    { key: 'other', label: '📁 Other Accounts', icon: '📁' }
  ];

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(Number(e.target.value))}
      className={className}
    >
      <option value="">{placeholder}</option>
      {accountTypeOrder.map(typeInfo => {
        const accountsOfType = groupedAccounts[typeInfo.key] || [];
        if (accountsOfType.length === 0) return null;
        
        return (
          <React.Fragment key={typeInfo.key}>
            <optgroup label={typeInfo.label}>
              {accountsOfType.map(account => (
                <option key={account.id} value={account.id}>
                  {account.institution} - {account.name}
                </option>
              ))}
            </optgroup>
          </React.Fragment>
        );
      })}
      {/* Show any accounts that don't match our predefined types */}
      {Object.entries(groupedAccounts).map(([type, accountsOfType]) => {
        if (accountTypeOrder.some(t => t.key === type)) return null;
        if (accountsOfType.length === 0) return null;
        
        return (
          <optgroup key={type} label={`📁 ${type.charAt(0).toUpperCase() + type.slice(1)}`}>
            {accountsOfType.map(account => (
              <option key={account.id} value={account.id}>
                {account.institution} - {account.name}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
};

export default GroupedAccountSelect;
